import { useEffect, useState } from 'react';
import {
  Text, Button,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  tokens,
} from '@fluentui/react-components';
import { useT } from '../../i18n';
import { MapView } from './MapView';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (lat: number, lon: number) => void;
  /** Punto ya elegido (si existe, el mapa abre ahí con el marcador puesto). */
  initialLat?: number;
  initialLon?: number;
  /** Centro de respaldo (ej. el rancho) cuando todavía no hay punto elegido. */
  fallbackLat?: number;
  fallbackLon?: number;
  /** Título del diálogo; por defecto "Ver en mapa" (rancho). El lote pasa su nombre. */
  title?: string;
}

/**
 * Selector de punto en mapa (lat/lon). IMPORTANTE: el caller debe renderizarlo como
 * HERMANO del Dialog del formulario, nunca dentro de su DialogSurface — anidado rompe
 * el focus-trap y deja el teclado muerto en el formulario al cerrarlo (PR #305).
 */
export function MapPointPickerDialog({
  open, onClose, onConfirm, initialLat, initialLon, fallbackLat, fallbackLon, title,
}: Props) {
  const t = useT();
  const [point, setPoint] = useState<[number, number] | null>(null);

  // Cada vez que se abre, parte del punto actual del formulario (si lo hay).
  useEffect(() => {
    if (open) setPoint(initialLat != null && initialLon != null ? [initialLat, initialLon] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const [recenterTo, setRecenterTo] = useState<{ center: [number, number]; zoom: number } | null>(null);

  // Al reabrir se limpia el estado de geolocalización (nunca se autollena al abrir).
  useEffect(() => {
    if (open) { setLocating(false); setLocError(null); setRecenterTo(null); }
  }, [open]);

  const useMyLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocError(t.ranchosAdmin.locUnsupported);
      return;
    }
    setLocating(true);
    setLocError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setPoint(p);
        setRecenterTo({ center: p, zoom: 17 });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocError(
          err.code === 1 ? t.ranchosAdmin.locDenied
          : err.code === 3 ? t.ranchosAdmin.locTimeout
          : t.ranchosAdmin.locUnavailable,
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const center: [number, number] | undefined =
    initialLat != null && initialLon != null ? [initialLat, initialLon]
    : fallbackLat != null && fallbackLon != null ? [fallbackLat, fallbackLon]
    : undefined;

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '640px' }}>
        <DialogBody>
          <DialogTitle>{title ?? t.ranchosAdmin.useMapBtn}</DialogTitle>
          <DialogContent>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.ranchosAdmin.pickPointHint}</Text>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button size="small" appearance="outline" disabled={locating} onClick={useMyLocation}>
                {locating ? t.ranchosAdmin.locLocating : t.ranchosAdmin.useMyLocation}
              </Button>
              {locError && <Text size={200} role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>{locError}</Text>}
            </div>
            <div style={{ height: '360px', marginTop: '8px' }}>
              <MapView
                features={point ? [{ id: 'picked', name: '', latitude: point[0], longitude: point[1] }] : []}
                drawMode
                singlePointMode
                initialCenter={center}
                initialZoom={center ? 15 : undefined}
                recenterTo={recenterTo}
                onDrawPointsChange={(pts) => setPoint(pts[0] ?? null)}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>{t.ranchosAdmin.cancel}</Button>
            <Button appearance="primary" disabled={!point} onClick={() => { if (point) { onConfirm(point[0], point[1]); onClose(); } }}>
              {t.ranchosAdmin.confirmPoint}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
